import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FDConfirmationDialog,
  FDIcon, FDSecondaryActionButton, FDSecondaryActionIconButton, FDStack,
} from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { FORCE_FOCUS_MODE_QUERY_PARAM, useLayoutContext } from 'Context/LayoutContext';
import { dataAttrs } from 'Helpers/Testing';
import useQueryParams from 'Hooks/useQueryParams';
import { useNavigationContext } from 'Organisms/NavigationWithContext/NavigationContext';
import { categories, eventTypes, eventSubTypes } from 'EventTracking/useEventTrackingLogEvent';

export const GuidedHouseholdReviewLayoutHeader = ({
  condensed, hideTitle, logStandardizedEvent, resourceType,
}) => {
  const { isFocusedLayout, setFocusedLayout, setNavigationVisible } = useLayoutContext();
  const { open, setOpen } = useNavigationContext();
  const [queryParams, setQuery] = useQueryParams();
  const [showExitFocusModeConfirmation, setShowExitFocusModeConfirmation] = useState(false);
  const enterFocusMode = useCallback(() => {
    setFocusedLayout(true);
    setNavigationVisible(false);
    queryParams.set(FORCE_FOCUS_MODE_QUERY_PARAM, 'true');
    setQuery(queryParams);
    logStandardizedEvent(categories.pm1OrPM2, eventTypes.fullScreen, eventSubTypes.start, { eventProperties: { resourceType } });
  }, [setFocusedLayout, setNavigationVisible, setQuery, queryParams]);
  const exitFocusMode = useCallback(() => {
    setShowExitFocusModeConfirmation(false);
    queryParams.set(FORCE_FOCUS_MODE_QUERY_PARAM, 'false');
    setQuery(queryParams);
    setFocusedLayout(false);
    setNavigationVisible(true);
    logStandardizedEvent(categories.pm1OrPM2, eventTypes.fullScreen, eventSubTypes.stop, { eventProperties: { resourceType } });
  }, [setFocusedLayout, setNavigationVisible, setQuery, queryParams]);
  const cancelExitingFocusMode = useCallback(() => {
    setShowExitFocusModeConfirmation(false);
    logStandardizedEvent(categories.pm1OrPM2, eventTypes.fullScreen, eventSubTypes.canceledStop, { eventProperties: { resourceType } });
  }, []);

  return (
    <FDStack direction="row" justifyContent="space-between" alignSelf="flex-start" alignItems="center" width="100%">
      <FDStack width="30%" direction="row" justifyContent="flex-start" alignItems="center">
        <FDSecondaryActionIconButton
          icon="bars"
          onClick={() => setOpen(true)}
          disabled={open}
          {...dataAttrs('guided-household-review-menu')}
        />
      </FDStack>
      <FDStack width="40%" direction="row" justifyContent="center" alignItems="center">
        {condensed && !hideTitle ? <MetaControlField attr="title" /> : <FDIcon icon="fac" />}
      </FDStack>
      <FDConfirmationDialog
        open={showExitFocusModeConfirmation}
        onClose={cancelExitingFocusMode}
        onCancel={cancelExitingFocusMode}
        onConfirm={exitFocusMode}
        title="Exit full screen view"
        message="Are you sure you'd like to exit this view?"
        confirmLabel="Yes"
      />
      <FDStack width="30%" direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
        {isFocusedLayout ? (
          <FDSecondaryActionButton
            size="sm"
            label="Exit full screen"
            onClick={() => setShowExitFocusModeConfirmation(true)}
            {...dataAttrs('toggle-focus-mode')}
          />
        ) : (
          <FDSecondaryActionButton
            size="sm"
            label="Full screen"
            // icon="zoomIn"
            onClick={enterFocusMode}
            {...dataAttrs('toggle-focus-mode')}
          />
        )}
      </FDStack>
    </FDStack>
  );
};

GuidedHouseholdReviewLayoutHeader.propTypes = {
  condensed: PropTypes.bool,
  hideTitle: PropTypes.bool,
};
